import { NavLink, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { NavUser } from "@/components/nav-user";
import { useAuthStore } from "@/stores/auth-store";
import { STRINGS } from "@/constants/strings";
import type { SidebarItem, ShellType } from "@/routes";
import type { ComponentProps } from "react";

interface AppSidebarProps extends ComponentProps<typeof Sidebar> {
  shell: ShellType;
  items: SidebarItem[];
}

export function AppSidebar({ shell, items, ...props }: AppSidebarProps) {
  const location = useLocation();
  const role = useAuthStore((s) => s.role);
  const tenantName = useAuthStore((s) => s.tenantName);

  const shellLabel =
    shell === "admin"
      ? STRINGS.shell.admin
      : shell === "patient"
        ? STRINGS.shell.patient
        : tenantName ?? STRINGS.shell.tenant;

  const visibleItems = role
    ? items.filter((item) => item.allowedRoles.includes(role))
    : items;

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <div>
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground text-xs font-bold">
                  O
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">
                    {STRINGS.app.name}
                  </span>
                  <span className="truncate text-xs">{shellLabel}</span>
                </div>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{STRINGS.nav.dashboard}</SidebarGroupLabel>
          <SidebarMenu>
            {visibleItems.map((item) => {
              const isActive =
                item.path === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(item.path);

              return (
                <SidebarMenuItem key={item.path}>
                  <SidebarMenuButton
                    asChild
                    tooltip={item.title}
                    isActive={isActive}
                  >
                    <NavLink to={item.path}>
                      {item.icon}
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
